import { Box, Button, Stack } from "@mui/material"
import type { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro"
import { Link } from "react-router-dom"
import { DataGrid } from "@core/components/dataGrid/dataGrid"
import { RefreshTableButton } from "../common/refreshTableButton"
import { usePostBotListState } from "../../hooks/usePostBotListState"
import { publicationPath } from "../../hooks/publicationSessionDraft"
import type { PublicationListItem } from "../../interface/publications"
import { useGetPublicationsQuery } from "../../services/publications/publicationsApi"
import {
  TemplateQueryFeedback,
  useTemplateRowCount,
} from "../templates/templateQueryFeedback"
import { PublicationFilters } from "./publicationFilters"

export interface PublicationsTableProps {
  listKey?: string
  /** Controlled explicit selection across loaded pages; no bulk mutation here. */
  selection?: GridRowSelectionModel
  onSelectionChange?: (ids: GridRowSelectionModel) => void
}
export function PublicationsTable({
  listKey = "publications",
  selection,
  onSelectionChange,
}: PublicationsTableProps) {
  const list = usePostBotListState({ resource: "publications", key: listKey })
  const query = useGetPublicationsQuery(list.listArgs)
  const page = !query.error ? query.currentData : undefined
  const rowCount = useTemplateRowCount(
    JSON.stringify(list.filters),
    page?.total,
  )
  const columns = (
    [
      {
        field: "name",
        headerName: "Публикация",
        minWidth: 240,
        flex: 1,
        renderCell: ({ row }) => (
          <Button component={Link} to={publicationPath(row.id)}>
            {row.name}
          </Button>
        ),
      },
      { field: "templateVersionId", headerName: "ID версии", width: 310 },
      { field: "revision", headerName: "Ревизия", width: 100 },
    ] satisfies GridColDef<PublicationListItem>[]
  ).map((column) => ({ ...column, sortable: false, filterable: false }))
  return (
    <Stack spacing={2}>
      <PublicationFilters {...list.filters} onChange={list.setFilters} />
      <TemplateQueryFeedback
        query={query}
        empty={page?.data.length === 0 && "Публикаций нет"}
      />
      <RefreshTableButton
        label="Обновить публикации"
        disabled={query.isFetching}
        onClick={() => void query.refetch()}
      />
      <Box sx={{ width: "100%", minHeight: 200 }}>
        <DataGrid
          {...list.gridProps}
          rows={page?.data ?? []}
          columns={columns}
          rowCount={rowCount}
          loading={query.isFetching}
          autoHeight
          disableRowSelectionOnClick
          checkboxSelection={!!onSelectionChange}
          rowSelectionModel={selection}
          onRowSelectionModelChange={onSelectionChange}
          keepNonExistentRowsSelected
        />
      </Box>
    </Stack>
  )
}
